import * as React from 'react';
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useBatchCreateAccounts, useOperatorOptions } from '@/lib/api/accounts-queries';
import { checkAccountCodes } from '@/lib/api/accounts';
import { useAdPlatformList } from '@/lib/api/ad-platforms-queries';
import { useAdProviderList } from '@/lib/api/ad-providers-queries';
import { useToast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils';

interface ParsedRow {
  lineNo: number;
  accountCode: string;
  accountName: string;
  amount: string;
  error: string | null;
  status: 'pending' | 'created' | 'failed';
  resultError?: string | null;
}

function parseBatchRows(rawText: string): { rows: ParsedRow[]; truncated: boolean } {
  const lines = rawText
    .split(/\r?\n/)
    .map((line, index) => ({ text: line.trim(), lineNo: index + 1 }))
    .filter((line) => line.text.length > 0);

  const parsed = lines.slice(0, 20).map(({ text, lineNo }) => {
    const parts = text.split(',');
    if (parts.length < 2) {
      return {
        lineNo,
        accountCode: text,
        accountName: '',
        amount: '0',
        error: '格式需为：账户ID,账户名称[,金额]',
        status: 'pending' as const,
      };
    }
    const accountCode = (parts[0] ?? '').trim();
    const accountName = (parts[1] ?? '').trim();
    const rawAmount = parts[2]?.trim() ?? '';
    let error: string | null = null;
    if (!accountCode) {
      error = '账户ID不能为空';
    } else if (!accountName) {
      error = '账户名称不能为空';
    } else if (rawAmount && (isNaN(Number(rawAmount)) || Number(rawAmount) < 0)) {
      error = '金额需为非负数';
    }
    const amount = rawAmount && !error ? rawAmount : '0';
    return { lineNo, accountCode, accountName, amount, error, status: 'pending' as const };
  });

  // 组内重复账户ID校验
  const codeCount = new Map<string, number>();
  for (const row of parsed) {
    if (!row.error && row.accountCode) {
      codeCount.set(row.accountCode, (codeCount.get(row.accountCode) ?? 0) + 1);
    }
  }
  const duplicateCodes = new Set(
    [...codeCount.entries()].filter(([, n]) => n > 1).map(([code]) => code),
  );
  const rows =
    duplicateCodes.size > 0
      ? parsed.map((row) =>
          !row.error && duplicateCodes.has(row.accountCode) ? { ...row, error: '账户ID重复' } : row,
        )
      : parsed;

  return { truncated: lines.length > 20, rows };
}

export function BatchCreateDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [platformId, setPlatformId] = React.useState('');
  const [providerId, setProviderId] = React.useState('');
  const [assignedOperatorId, setAssignedOperatorId] = React.useState('');
  const [rawText, setRawText] = React.useState('');
  const [rows, setRows] = React.useState<ParsedRow[]>([]);
  const { toast } = useToast();
  const batchCreate = useBatchCreateAccounts();
  const [isChecking, setIsChecking] = React.useState(false);
  const { data: platforms } = useAdPlatformList({ pageNo: 1, pageSize: 100, status: 1 });
  const { data: providers } = useAdProviderList({ pageNo: 1, pageSize: 100, status: 1 });
  const { data: operators = [] } = useOperatorOptions();

  React.useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const parsed = parseBatchRows(rawText);
      if (parsed.truncated) {
        toast({ description: '单次最多录入 20 行，已截断预览' });
      }
      setRows(parsed.rows);
      setIsChecking(false);

      const codesToCheck = parsed.rows
        .filter((r) => !r.error && r.accountCode)
        .map((r) => r.accountCode);

      if (codesToCheck.length === 0) return;

      setIsChecking(true);
      try {
        const existingCodes = await checkAccountCodes(codesToCheck);
        if (!cancelled && existingCodes.length > 0) {
          const existingSet = new Set(existingCodes);
          setRows((prev) =>
            prev.map((row) =>
              !row.error && existingSet.has(row.accountCode)
                ? { ...row, error: '广告账户ID已存在' }
                : row,
            ),
          );
        }
      } catch {
        // 检查失败不阻断流程，提交时后端唯一约束兜底
      } finally {
        if (!cancelled) setIsChecking(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [rawText, toast]);

  const reset = () => {
    setPlatformId('');
    setProviderId('');
    setAssignedOperatorId('');
    setRawText('');
    setRows([]);
    setIsChecking(false);
    batchCreate.reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const validRows = rows.filter((row) => !row.error);
  const canSubmit = Boolean(
    platformId && providerId && validRows.length > 0 && !batchCreate.isPending && !isChecking,
  );

  const handleSubmit = () => {
    if (!canSubmit) return;
    const freshRows = parseBatchRows(rawText).rows;
    const freshValidRows = freshRows.filter((row) => !row.error);
    setRows(freshRows);
    if (!freshValidRows.length) return;

    batchCreate.mutate(
      {
        platformId: Number(platformId),
        providerId: Number(providerId),
        assignedOperatorId: assignedOperatorId ? Number(assignedOperatorId) : undefined,
        accounts: freshValidRows.map((row) => ({
          accountCode: row.accountCode,
          accountName: row.accountName,
          ...(row.amount && Number(row.amount) > 0 ? { initialAmount: row.amount } : {}),
        })),
      },
      {
        onSuccess: (results) => {
          const resultMap = new Map(results.map((item) => [item.accountCode, item]));
          const nextRows = rows.map((row) => {
            if (row.error) return row;
            const result = resultMap.get(row.accountCode);
            return {
              ...row,
              status: result?.status ?? 'failed',
              resultError: result?.error ?? (result ? null : '后端未返回该行结果'),
            };
          });
          setRows(nextRows);
          const submitRows = nextRows.filter((r) => !r.error);
          if (submitRows.length > 0 && submitRows.every((r) => r.status === 'created')) {
            toast({ description: `已创建 ${submitRows.length} 个广告账户` });
            handleClose();
          }
        },
        onError: (err) => {
          toast({ variant: 'destructive', description: err.message });
        },
      },
    );
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => !nextOpen && !batchCreate.isPending && handleClose()}
    >
      <SheetContent className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[760px]">
        <SheetHeader className="space-y-1 border-b px-6 py-4">
          <SheetTitle>批量新建广告账户</SheetTitle>
          <SheetDescription>
            每行输入一个广告账户，格式为：账户ID,账户名称,金额（可选）。
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="batch-platform">广告平台</Label>
              <Select value={platformId} onValueChange={setPlatformId}>
                <SelectTrigger id="batch-platform">
                  <SelectValue placeholder="选择广告平台" />
                </SelectTrigger>
                <SelectContent>
                  {(platforms?.records ?? []).map((platform) => (
                    <SelectItem key={platform.platformId} value={String(platform.platformId)}>
                      {platform.platformName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="batch-provider">广告户商</Label>
              <Select value={providerId} onValueChange={setProviderId}>
                <SelectTrigger id="batch-provider">
                  <SelectValue placeholder="选择广告户商" />
                </SelectTrigger>
                <SelectContent>
                  {(providers?.records ?? []).map((provider) => (
                    <SelectItem key={provider.providerId} value={String(provider.providerId)}>
                      {provider.providerName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="batch-operator">分配投手（可选）</Label>
            <Select value={assignedOperatorId} onValueChange={setAssignedOperatorId}>
              <SelectTrigger id="batch-operator">
                <SelectValue placeholder="选择批次共用投手" />
              </SelectTrigger>
              <SelectContent>
                {operators.map((operator) => (
                  <SelectItem key={operator.userId} value={String(operator.userId)}>
                    {operator.displayName
                      ? `${operator.displayName} (${operator.username})`
                      : operator.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="batch-raw">账户列表</Label>
            <textarea
              id="batch-raw"
              className="min-h-32 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              placeholder={'abc123,主账户,1000\ndef456,备用账户'}
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>预览</Label>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {isChecking && <Loader2 className="h-3 w-3 animate-spin" />}
                合法 {validRows.length} / {rows.length}
              </span>
            </div>
            <div className="overflow-hidden rounded-md border">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b bg-muted/50 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="w-10 px-3 py-2 text-left font-medium">行</th>
                    <th className="px-3 py-2 text-left font-medium">账户 ID</th>
                    <th className="px-3 py-2 text-left font-medium">账户名称</th>
                    <th className="w-24 px-3 py-2 text-right font-medium">金额 (USD)</th>
                    <th className="w-40 px-3 py-2 text-left font-medium">状态</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td className="px-3 py-10 text-center text-muted-foreground" colSpan={5}>
                        输入账户后显示预览
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr
                        key={`${row.lineNo}-${row.accountCode}`}
                        className={cn(
                          'border-b transition-colors last:border-0 hover:bg-muted/30',
                          row.error && 'bg-destructive/5',
                        )}
                      >
                        <td className="px-3 py-2 tabular-nums text-muted-foreground">
                          {row.lineNo}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{row.accountCode || '-'}</td>
                        <td className="px-3 py-2">{row.accountName || '-'}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">
                          {Number(row.amount) > 0 ? row.amount : '-'}
                        </td>
                        <td className="px-3 py-2">
                          <RowStatus row={row} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <SheetFooter className="border-t px-6 py-4">
          <Button variant="outline" onClick={handleClose} disabled={batchCreate.isPending}>
            取消
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {batchCreate.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            批量创建（{validRows.length}条）
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function RowStatus({ row }: { row: ParsedRow }) {
  if (row.error) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-destructive">
        <AlertTriangle className="h-3.5 w-3.5" />
        {row.error}
      </span>
    );
  }
  if (row.status === 'created') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-success">
        <CheckCircle2 className="h-3.5 w-3.5" />
        已创建
      </span>
    );
  }
  if (row.status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-destructive">
        <XCircle className="h-3.5 w-3.5" />
        失败{row.resultError ? `：${row.resultError}` : ''}
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">待创建</span>;
}
